import { User, UserRole } from '../types/models';

/**
 * Checks if the user has the specified role
 * @param user The user object
 * @param role The role to check for
 * @returns True if the user has the specified role, false otherwise
 */
export const hasRole = (user: User | null, role: UserRole): boolean => {
  if (!user || !user.roles) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * Checks if the user has admin role
 * @param user The user object
 * @returns True if the user has admin role, false otherwise
 */
export const isAdmin = (user: User | null): boolean => {
  return hasRole(user, UserRole.ADMIN);
};

/**
 * Checks if the user has issuer role
 * @param user The user object
 * @returns True if the user has issuer role, false otherwise
 */
export const isIssuer = (user: User | null): boolean => {
  return hasRole(user, UserRole.ISSUER);
};

/**
 * Checks if the user has cm_pic role
 * @param user The user object
 * @returns True if the user has cm_pic role, false otherwise
 */
export const isCmPic = (user: User | null): boolean => {
  return hasRole(user, UserRole.CM_PIC);
};

/**
 * Checks if the user has reviewer role
 * @param user The user object
 * @returns True if the user has reviewer role, false otherwise
 */
export const isReviewer = (user: User | null): boolean => {
  return hasRole(user, UserRole.REVIEWER);
};

/**
 * Checks if the user has approver role
 * @param user The user object
 * @returns True if the user has approver role, false otherwise
 */
export const isApprover = (user: User | null): boolean => {
  return hasRole(user, UserRole.APPROVER);
};

/**
 * Checks if the user is the system admin user that should be hidden from the user table
 * @param user The user object
 * @returns True if the user is a system admin user, false otherwise
 */
export const isSystemAdminUser = (user: User | null): boolean => {
  if (!user) {
    return false;
  }
  
  // Check if the is_system_admin flag is available from the backend
  if ('is_system_admin' in user) {
    return user.is_system_admin === true;
  }
  
  // Fallback to the old logic if the flag is not available
  if (!user.roles) {
    return false;
  }
  return (user.full_name === "Admin User" || user.full_name === "Super User") && 
         user.roles.length === 1 && 
         user.roles.includes(UserRole.ADMIN);
};
